import { CommonModule, DOCUMENT } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';

import { HxBaseControl } from '../../base';
import { HxIcon } from '../icon/icon';
import { HxLogo } from '../logo/logo';
import { HxPopoverDirective } from '../popover/popover';
import { HxTooltipDirective } from '../tooltip/tooltip';

/** Application item for the switcher */
export interface AppSwitcherItem {
  /** Name/label of the application */
  name: string;
  /** URL to navigate to when clicked */
  url: string;
  /** Whether this app is currently active */
  active?: boolean;
  /** Logo type: 'company' or 'product' (if not set, uses icon from logo field) */
  logoType?: 'company' | 'product';
  /** Product identifier for product logos (symphony, codis, phoenix, apollo, scheduling) */
  logoProduct?: 'symphony' | 'codis' | 'phoenix' | 'apollo' | 'scheduling';
}

/** Event payload when an app is clicked */
export interface AppSwitcherClickEvent {
  item: AppSwitcherItem;
  timestamp: Date;
  nativeEvent: MouseEvent | KeyboardEvent;
}

/**
 * # Harmony App Switcher Component
 *
 * A responsive application switcher for navigating between ComplexCare suite applications.
 *
 * ## Features
 *
 * - **Vertical toggle**: Show/hide the application list on demand
 * - **Responsive collapse**: Icon-only view for compact displays
 * - **Configurable apps**: Pass logo, name, and URL for each application
 * - **Hover effects**: Visual feedback on interaction
 * - **Keyboard accessible**: Full WCAG 2.2 AA compliance
 * - **Popover-based**: Uses HxPopover for positioning and backdrop handling
 *
 * ## Usage Example
 *
 * ```html
 * <hx-app-switcher [apps]="applications" [collapsed]="false" (appClick)="onAppSwitch($event)"> </hx-app-switcher>
 * ```
 *
 * ```typescript
 * applications: AppSwitcherItem[] = [
 *   { name: 'Phoenix', url: '/phoenix', logoType: 'product', logoProduct: 'phoenix' },
 *   { name: 'Apollo', url: '/apollo', logoType: 'product', logoProduct: 'apollo' }
 * ];
 * ```
 */
@Component({
  selector: 'hx-app-switcher',
  standalone: true,
  imports: [CommonModule, HxIcon, HxLogo, HxPopoverDirective, HxTooltipDirective],
  templateUrl: './app-switcher.html',
  styleUrls: ['./app-switcher.css'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.hx-app-switcher]': 'true',
    '[class.hx-app-switcher--collapsed]': 'collapsed()',
    '[class]': 'customClassName()',
  },
})
export class HxAppSwitcher extends HxBaseControl {
  private readonly _document = inject(DOCUMENT);

  protected readonly popoverContent = viewChild.required<TemplateRef<unknown>>('popoverContent');
  protected readonly triggerButton = viewChild.required<ElementRef<HTMLButtonElement>>('triggerButton');
  protected readonly popoverDirective = viewChild(HxPopoverDirective);

  /** Track if popover is open */
  protected readonly isPopoverOpen = signal<boolean>(false);

  constructor() {
    super();
    this._setupClickOutsideListener();
  }

  /** Setup click outside listener to close popover */
  private _setupClickOutsideListener(): void {
    afterNextRender(() => {
      const clickHandler = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        const host = this.triggerButton()?.nativeElement;

        if (this.isPopoverOpen() && host && !host.contains(target)) {
          this.isPopoverOpen.set(false);
        }
      };

      this._document.addEventListener('click', clickHandler);

      this._destroyRef.onDestroy(() => {
        this._document.removeEventListener('click', clickHandler);
      });
    });
  }

  /** List of applications to display in the switcher */
  readonly apps = input.required<AppSwitcherItem[]>();

  /**
   * Whether the switcher is in collapsed (icon-only) state
   *
   * Default: `false`
   */
  readonly collapsed = input<boolean>(false);

  /**
   * Title/header text for the switcher panel
   *
   * Default: `'Switch Application'`
   */
  readonly panelTitle = input<string>('Switch Application');

  /**
   * Icon for the switcher toggle button
   *
   * Default: `'grid-3x3-gap'`
   */
  readonly toggleIcon = input<string>('grid-3x3-gap');

  /**
   * Popover position
   *
   * Default: `'center'`
   */
  readonly popoverPosition = input<
    'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'center'
  >('center');

  /** Emitted when an application is clicked */
  readonly appClick = output<AppSwitcherClickEvent>();

  /** Custom class name for styling */
  protected readonly customClassName = computed(() => this.cssClass() || '');

  /** Handle app item click */
  protected handleAppClick(item: AppSwitcherItem, event: MouseEvent | KeyboardEvent): void {
    if (this.isDisabled()) {
      return;
    }

    const clickEvent: AppSwitcherClickEvent = {
      item,
      timestamp: new Date(),
      nativeEvent: event,
    };

    this.appClick.emit(clickEvent);

    // Navigate to the URL in a new tab if provided
    if (item.url) {
      window.open(item.url, '_blank');
    }
  }

  /** Handle keyboard events for accessibility */
  protected handleKeyDown(item: AppSwitcherItem, event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleAppClick(item, event);
    }
  }

  /** Check if component is disabled */
  protected isDisabled(): boolean {
    return this.disabled() || false;
  }

  /** Close the popover using the directive's API */
  protected closePopover(): void {
    this.isPopoverOpen.set(false);
    const popover = this.popoverDirective();
    if (popover && typeof popover.hide === 'function') {
      popover.hide();
    } else {
      // Fallback: trigger button click if directive doesn't expose hide()
      const button = this.triggerButton()?.nativeElement;
      if (button) {
        button.click();
      }
    }
  }

  /** Open popover when button is clicked */
  protected openPopover(): void {
    this.isPopoverOpen.set(true);
  }

  /** Handle keyboard events on popover header */
  protected handlePopoverHeaderKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.closePopover();
    }
  }
}
